import { useEffect, useRef } from "react";
import { cn } from "@wa/ui";
import { attachRemoteSink } from "../pilot/capture";
import { buildBands, easeLevels, readLevels, type Band } from "./spectrum";

/** How many bars. Enough to read as a spectrum, few enough to stay a background. */
const BARS = 44;

/** 1024 points at 48 kHz is ~47 Hz per bin - a band wide enough to see a vowel move. */
const FFT_SIZE = 1024;

/**
 * The analyser's own decibel window.
 *
 * Tighter than the -100/-30 default, and that is what makes it a phone meter
 * rather than a studio one. A conversation on a Twilio leg sits around -50 dBFS,
 * which on the default window is the bottom fifth of the byte range - the bars
 * would barely leave the floor while two people talk perfectly audibly.
 */
const MIN_DB = -78;
const MAX_DB = -22;

/** Per-frame smoothing. See `easeLevels` - fast up, slow down. */
const RISE = 0.55;
const FALL = 0.11;

/** The bars rest here rather than at nothing: a flat line reads as a broken meter. */
const IDLE = 0.1;

/** The SDK publishes the remote track a moment after the call connects. */
const STREAM_WAIT_MS = 12_000;
const STREAM_POLL_MS = 350;

type Streams = { local: MediaStream | null; remote: MediaStream | null } | null;

/**
 * The live call as a spectrum, drawn behind the running seconds.
 *
 * It is a BACKGROUND, so it is built out of DOM rather than canvas: the bars are
 * coloured by the stylesheet, which means they follow the theme, both colour
 * schemes and every future palette change for free. A canvas would need the
 * colour resolved in JavaScript, and the resolved value of an `oklch` variable
 * is a fight this app has already had once (see the Gallery Hall widget).
 *
 * Only `transform` is written per frame. Height, width and layout are the
 * stylesheet's, so sixty frames a second costs compositing and nothing else -
 * this thing sits under a timer during a phone call and must never be the reason
 * the hang-up button feels sticky.
 *
 * Both sides of the call go in. The operator's own microphone is not the
 * interesting half, but a meter that only moves while the CUSTOMER talks looks
 * broken during the half of the call you are doing the talking.
 */
export function CallWave({
  active,
  paused,
  getStreams,
  className,
}: {
  /** The call is up. Before that there is nothing to analyse. */
  active: boolean;
  /** On hold: the customer is out of the mix, so the meter rests. */
  paused: boolean;
  getStreams: () => Streams;
  className?: string;
}) {
  const barsRef = useRef<HTMLElement[]>([]);
  // Read through a ref inside the frame loop rather than closed over: the
  // provider hands out a new function identity on every state change, and a
  // dependency on it would tear the audio graph down and rebuild it once a
  // second for the whole call.
  const streamsRef = useRef(getStreams);
  streamsRef.current = getStreams;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  useEffect(() => {
    if (!active) return;
    if (typeof window === "undefined" || typeof window.AudioContext !== "function") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    let cancelled = false;
    let frame = 0;
    let ctx: AudioContext | null = null;
    let detachSink: (() => void) | null = null;

    void (async () => {
      // Same wait the tap does, and for the same reason: an immediate read after
      // the call goes active is legitimately empty, and giving up on it would
      // leave a flat meter for a call that has perfectly good audio.
      const deadline = Date.now() + STREAM_WAIT_MS;
      let streams = streamsRef.current();
      while (!cancelled && (!streams?.local || !streams.remote) && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, STREAM_POLL_MS));
        streams = streamsRef.current();
      }
      if (cancelled || !streams?.local) return;

      try {
        ctx = new AudioContext();
        if (ctx.state === "suspended") await ctx.resume();
      } catch {
        // No meter. Nothing else about the call is affected, and a call window
        // that threw here would be a far worse outcome than a still background.
        return;
      }
      if (cancelled) {
        void ctx.close().catch(() => {});
        return;
      }

      const analyser = ctx.createAnalyser();
      analyser.fftSize = FFT_SIZE;
      analyser.minDecibels = MIN_DB;
      analyser.maxDecibels = MAX_DB;
      // The analyser does its own averaging between frames. Kept low, because
      // `easeLevels` below is where the smoothing is supposed to happen and two
      // filters in series just add lag.
      analyser.smoothingTimeConstant = 0.35;

      const mix = ctx.createGain();
      mix.connect(analyser);
      try {
        ctx.createMediaStreamSource(streams.local).connect(mix);
      } catch {
        // A track that ended between the poll above and here.
      }
      if (streams.remote && streams.remote.getAudioTracks().length > 0) {
        detachSink = attachRemoteSink(streams.remote);
        try {
          ctx.createMediaStreamSource(streams.remote).connect(mix);
        } catch {
          // Same: the far end hung up mid-setup. The near half still draws.
        }
      }

      // A node nothing pulls from is a node that never runs - the graph is only
      // driven from the destination. Zero gain, so this adds a path to the
      // speakers without adding a sound. The tap in `capture.ts` does the same.
      const silence = ctx.createGain();
      silence.gain.value = 0;
      analyser.connect(silence);
      silence.connect(ctx.destination);

      const bands: Band[] = buildBands(BARS, analyser.frequencyBinCount, ctx.sampleRate);
      const freq = new Uint8Array(analyser.frequencyBinCount);
      const targets = new Float32Array(BARS);
      const levels = new Float32Array(BARS).fill(IDLE);

      const draw = () => {
        if (cancelled) return;
        if (pausedRef.current) {
          targets.fill(0);
        } else {
          analyser.getByteFrequencyData(freq);
          readLevels(freq, bands, targets);
        }
        easeLevels(levels, targets, RISE, FALL);
        const bars = barsRef.current;
        for (let i = 0; i < bars.length; i++) {
          const bar = bars[i];
          if (!bar) continue;
          const level = IDLE + (levels[i] ?? 0) * (1 - IDLE);
          bar.style.transform = `scaleY(${level.toFixed(3)})`;
        }
        frame = requestAnimationFrame(draw);
      };
      frame = requestAnimationFrame(draw);
    })();

    return () => {
      cancelled = true;
      if (frame) cancelAnimationFrame(frame);
      detachSink?.();
      // Closed rather than suspended: a context left open holds an output device
      // for the rest of the session, and an operator makes a lot of calls.
      void ctx?.close().catch(() => {});
      for (const bar of barsRef.current) {
        if (bar) bar.style.transform = "";
      }
    };
  }, [active]);

  return (
    <div className={cn("crm-wavebg", active && "is-live", className)} aria-hidden="true">
      {Array.from({ length: BARS }, (_, i) => (
        <i
          key={i}
          ref={(el) => {
            if (el) barsRef.current[i] = el;
          }}
        />
      ))}
    </div>
  );
}
